import { IconSearch } from '@developerz/ui';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { useDebouncedSearch } from '../lib/debounced-search';
import type {
  RepoEnablementFacets,
  RepoEnablementFilter,
  RepoEnablementFilterPatch,
} from '../lib/repo-enablement';
import { count } from '../lib/time';
import { RepoEnablementFilterSelect } from './RepoEnablementFilterSelect';
import type { SelectOption } from './Select';

// The picker's filter row. Presenter: every filter is the server's (the list is a page of many, so
// a client-side filter would only search the page in hand). The language and topic options are the
// account's own facets; a `null` facets read leaves just "Any", and the rest still works.

const ANY = '';

const faceted = (rows: RepoEnablementFacets['languages'] | undefined): SelectOption<string>[] => [
  { value: ANY, label: t('repo_enablement.filter_any') },
  ...(rows ?? []).map((row) => ({ value: row.value, label: `${row.value} (${count(row.count)})` })),
];

export function RepoEnablementFilters(props: {
  readonly filter: RepoEnablementFilter;
  readonly facets: RepoEnablementFacets | undefined;
  readonly onChange: (patch: RepoEnablementFilterPatch) => void;
}): JSX.Element {
  const search = useDebouncedSearch();
  const text = (value: string): string | undefined => (value === ANY ? undefined : value);
  return (
    <div class="repo-enablement__filters">
      <div class="search-input repo-enablement__search">
        <IconSearch size="1rem" />
        <input
          type="search"
          class="input"
          placeholder={t('repo_enablement.search_placeholder')}
          aria-label={t('repo_enablement.search_aria')}
          value={search.qInput()}
          onInput={(e) => {
            const next = e.currentTarget.value;
            search.setQInput(next);
            search.debounce(next, (q) => props.onChange({ q: text(q) }));
          }}
        />
      </div>
      <RepoEnablementFilterSelect
        id="repo-enablement-language"
        label={t('repo_enablement.filter_language')}
        value={props.filter.language ?? ANY}
        options={faceted(props.facets?.languages)}
        onChange={(value) => props.onChange({ language: text(value) })}
      />
      <RepoEnablementFilterSelect
        id="repo-enablement-topic"
        label={t('repo_enablement.filter_topic')}
        value={props.filter.topic ?? ANY}
        options={faceted(props.facets?.topics)}
        onChange={(value) => props.onChange({ topic: text(value) })}
      />
      <RepoEnablementFilterSelect
        id="repo-enablement-visibility"
        label={t('repo_enablement.filter_visibility')}
        value={props.filter.visibility ?? ANY}
        options={[
          { value: ANY, label: t('repo_enablement.filter_any') },
          { value: 'public', label: t('repo_enablement.visibility_public') },
          { value: 'private', label: t('repo_enablement.visibility_private') },
        ]}
        onChange={(value) =>
          props.onChange({
            visibility: value === 'public' || value === 'private' ? value : undefined,
          })
        }
      />
      <RepoEnablementFilterSelect
        id="repo-enablement-activity"
        label={t('repo_enablement.filter_activity')}
        value={props.filter.active_within_days?.toString() ?? ANY}
        options={[
          { value: ANY, label: t('repo_enablement.filter_any') },
          { value: '30', label: t('repo_enablement.activity_30') },
          { value: '90', label: t('repo_enablement.activity_90') },
          { value: '365', label: t('repo_enablement.activity_365') },
        ]}
        onChange={(value) =>
          props.onChange({ active_within_days: value === ANY ? undefined : Number(value) })
        }
      />
      <RepoEnablementFilterSelect
        id="repo-enablement-state"
        label={t('repo_enablement.filter_state')}
        value={props.filter.enabled === undefined ? ANY : String(props.filter.enabled)}
        options={[
          { value: ANY, label: t('repo_enablement.filter_any') },
          { value: 'false', label: t('repo_enablement.state_off') },
          { value: 'true', label: t('repo_enablement.state_on') },
        ]}
        onChange={(value) =>
          props.onChange({ enabled: value === ANY ? undefined : value === 'true' })
        }
      />
    </div>
  );
}
